import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { removeFormField, selectAddressFormData, setFormField } from 'slices/addressFormSlice'
import { useAppDispatch } from 'store/hooks'
import { getObjectTypeOptions } from '../../../ObjectTypeOptions/ObjectTypeOptions'
import { BuildingType, HouseApartmentType, ObjectType } from 'types/ApiResponse'
// import moment from 'moment'
import {
  // StyleTopLine,
  StyledCol,
  StyledColContainer,
  StyledContinueButtonContainer,
  // StyledDatePicker,
  StyledLine,
  StyledObjectType,
  StyledObjectTypeRadio,
  StyledRowContainer,
} from './ValueObjectTypeForm.styled'
// import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers'
// import i18n from 'common/utils/i18n/i18n'
// import MomentUtils from '@date-io/moment'
import RadioGroupButtons from 'components/RadioGroupButtons/RadioGroupButtons'
import MultiSelect from 'components/MultiSelect/MultiSelect'
import { handleNumberInput, hausTypeEFHandMFH } from 'components/Modal/ProductFormModal/helper'
import Button from 'components/Button/Button'
import Input from 'components/Input/Input'
import { InputAdornment } from '@material-ui/core'

const getBuildingTypeOptions = (objectType: ObjectType, t) => {
  switch (objectType) {
    case ObjectType.HOUSE:
      return [
        {
          key: BuildingType.SINGLE_FAMILY_HOUSE,
          value: t(`buildingType.${BuildingType.SINGLE_FAMILY_HOUSE.toLowerCase()}`),
        },
        {
          key: BuildingType.SINGLE_FAMILY_HOUSE_WITH_GRANNY_APARTMENT,
          value: t(
            `buildingType.${BuildingType.SINGLE_FAMILY_HOUSE_WITH_GRANNY_APARTMENT.toLowerCase()}`
          ),
        },
        {
          key: BuildingType.TWO_FAMILY_HOUSE,
          value: t(`buildingType.${BuildingType.TWO_FAMILY_HOUSE.toLowerCase()}`),
        },
      ]
    case ObjectType.MULTI_FAMILY_HOUSE:
      return [
        {
          key: BuildingType.MULTI_FAMILY_HOUSE,
          value: t(`buildingType.${BuildingType.MULTI_FAMILY_HOUSE.toLowerCase()}`),
        },
        {
          key: BuildingType.RESIDENTIAL_AND_COMMERCIAL,
          value: t(`buildingType.${BuildingType.RESIDENTIAL_AND_COMMERCIAL.toLowerCase()}`),
          tooltip: t(
            `buildingType.${BuildingType.RESIDENTIAL_AND_COMMERCIAL.toLowerCase()}.tooltip`
          ),
        },
      ]
  }
  return []
}

const ValueObjectTypeForm = (props: any) => {
  const { setCurrentTab, setCurrentSubTab } = props
  const dispatch = useAppDispatch()
  const addressFormData = useSelector(selectAddressFormData)
  const { t } = useTranslation()
  const [showValidation, setShowValidation] = useState(false)
  const [validationErrors, setValidationErrors] = useState([])
  const isDE = addressFormData.addressData?.nation === 'DE'
  const objectTypeOptions = getObjectTypeOptions(isDE, t, true)
  const objectType = addressFormData.addressData?.propertyDetails?.objectType
  // const dateFormat = 'YYYY-MM-DD'

  const validateNumericField = (currentValue, min, max, key) => {
    const numericValue = parseFloat(currentValue.replace(',', '.'))
    const numericMin = parseInt(min)
    const numericMax = parseInt(max)
    if (numericValue >= numericMax) {
      dispatch(setFormField({ key, value: `${numericMax}` }))
    } else if (numericValue <= numericMin) {
      dispatch(setFormField({ key, value: `${numericMin}` }))
    }
  }

  // const handleChangeValuationDate = useCallback(
  //   (valuationDate: any) => {
  //     dispatch(setFormField({ key: 'valuationDate', value: valuationDate.format(dateFormat) }))
  //   },
  //   [dispatch]
  // )

  const handleChangeBuildingType = useCallback(
    (value: BuildingType) => {
      dispatch(setFormField({ key: 'propertyDetails.buildingType', value }))
    },
    [dispatch]
  )

  const handleChangeObjectType = useCallback(
    (value: ObjectType) => {
      dispatch(setFormField({ key: 'propertyDetails.objectType', value }))
      setShowValidation(false)
      setValidationErrors([])
      if (value === ObjectType.APARTMENT) {
        dispatch(
          setFormField({ key: 'propertyDetails.buildingType', value: BuildingType.APARTMENT })
        )
      }
      if (value === ObjectType.HOUSE) {
        handleChangeBuildingType(BuildingType.SINGLE_FAMILY_HOUSE)
      }
      if (value === ObjectType.MULTI_FAMILY_HOUSE) {
        handleChangeBuildingType(BuildingType.MULTI_FAMILY_HOUSE)
      }
      if (value !== ObjectType.HOUSE) {
        dispatch(removeFormField('propertyDetails.houseOrApartmentType'))
      }
    },
    [dispatch, handleChangeBuildingType]
  )

  const onChangeHouseType = (houseOrApartmentType: HouseApartmentType) => {
    dispatch(
      setFormField({ key: 'propertyDetails.houseOrApartmentType', value: houseOrApartmentType })
    )
  }

  const onChangePlotArea = (plotArea: string, validate: boolean = false) => {
    if (plotArea.length === 0) {
      dispatch(removeFormField('propertyDetails.plotArea'))
      return
    }
    const regex = /^\d*(,\d{0,2})?$/
    if (regex.test(plotArea)) {
      dispatch(setFormField({ key: 'propertyDetails.plotArea', value: plotArea }))
      if (validate) {
        validateNumericField(plotArea, 1, 5000, 'propertyDetails.plotArea')
      }
    }
  }

  const onContinueClick = () => {
    setShowValidation(true)
    if (validationErrors.length === 0) {
      if (objectType === ObjectType.PLOT_OF_LAND) {
        setCurrentTab((tab) => tab + 3)
      } else {
        setCurrentSubTab((tab) => tab + 1)
      }
    }
  }

  const validate = (name, property) => {
    if (property === null || property === undefined || property === '') {
      if (!validationErrors.includes(name)) {
        setValidationErrors((errors) => [name, ...errors])
      }
      return showValidation && true
    } else {
      if (validationErrors.includes(name)) {
        setValidationErrors((errors) => errors.filter((error) => error !== name))
      }
      return false
    }
  }

  const validationMultiSelect = (value, options, formFieldName) => {
    if (!options) {
      return value
    }
    const matchingOption = options.find(
      (option) => option.value === `${value}` || option.key === `${value}`
    )
    if (
      (value !== null && value !== undefined && !matchingOption) ||
      value === null ||
      value === undefined
    ) {
      dispatch(removeFormField(formFieldName))
      return false
    }
    return true
  }

  useEffect(() => {
    if (!addressFormData.addressData?.propertyDetails?.objectType) {
      handleChangeObjectType(ObjectType.HOUSE)
    }
    // if (!addressFormData.addressData?.valuationDate) {
    //   handleChangeValuationDate(moment())
    // }
  }, [addressFormData.addressData, handleChangeObjectType])

  return (
    <>
      <StyledObjectType>
        {/* <MuiPickersUtilsProvider locale={i18n.language} utils={MomentUtils}>
          <StyledDatePicker
            validationError={moment(addressFormData.addressData?.valuationDate).isBefore(
              '2022-01-01'
            )}
          >
            <DatePicker
              variant="inline"
              inputVariant="outlined"
              label={`${t('newAddressModal.valuationDate')}*`}
              value={moment(addressFormData.addressData?.valuationDate).format(dateFormat)}
              onChange={handleChangeValuationDate}
              helperText={`01.01.2022 – ${t('common.today')}`}
              format={'DD.MM.YYYY'}
              minDate="2022-01-01"
              maxDate={moment().format(dateFormat)}
            />
          </StyledDatePicker>
        </MuiPickersUtilsProvider>
        <StyleTopLine /> */}
        <StyledObjectTypeRadio>
          <RadioGroupButtons
            options={objectTypeOptions}
            label={`${t('newAddressModal.objectDataTab.objectType')}*`}
            onChangeProps={handleChangeObjectType}
            value={addressFormData.addressData?.propertyDetails?.objectType}
          />
        </StyledObjectTypeRadio>
        {objectType !== ObjectType.APARTMENT && <StyledLine />}
        {(objectType === ObjectType.HOUSE || objectType === ObjectType.MULTI_FAMILY_HOUSE) && (
          <StyledObjectTypeRadio>
            <RadioGroupButtons
              validationErrorCheckbox={validate(
                'buildingType',
                addressFormData.addressData?.propertyDetails?.buildingType
              )}
              options={getBuildingTypeOptions(objectType, t)}
              label={`${t('newAddressModal.objectDataTab.buildingType')}*`}
              onChangeProps={handleChangeBuildingType}
              value={addressFormData.addressData?.propertyDetails?.buildingType || ''}
              validation={validationMultiSelect}
              formFieldName="propertyDetails.buildingType"
            />
          </StyledObjectTypeRadio>
        )}
        {objectType === ObjectType.HOUSE && (
          <>
            <StyledLine />
            <StyledRowContainer>
              <StyledColContainer>
                <StyledCol
                  validationError={validate(
                    'houseOrApartmentType',
                    addressFormData.addressData?.propertyDetails?.houseOrApartmentType
                  )}
                >
                  <MultiSelect
                    value={addressFormData.addressData?.propertyDetails?.houseOrApartmentType || ''}
                    options={hausTypeEFHandMFH}
                    label={`${t('newAddressModal.objectDataTab.houseType')}*`}
                    onChange={(event) => onChangeHouseType(event.target.value)}
                    validation={validationMultiSelect}
                    formFieldName="propertyDetails.houseOrApartmentType"
                  />
                </StyledCol>
              </StyledColContainer>
            </StyledRowContainer>
          </>
        )}
        {objectType === ObjectType.PLOT_OF_LAND && (
          <>
            <StyledRowContainer>
              <StyledColContainer>
                <StyledCol
                  validationError={validate(
                    'plotArea',
                    addressFormData.addressData?.propertyDetails?.plotArea
                  )}
                >
                  <Input
                    value={addressFormData.addressData?.propertyDetails?.plotArea}
                    onChange={(event) => onChangePlotArea(event.target.value)}
                    onBlur={(event) => onChangePlotArea(event.target.value, true)}
                    onKeyPress={(event) => handleNumberInput(event, true)}
                    defaultValue={addressFormData.addressData?.propertyDetails?.plotArea}
                    label={`${t('newAddressModal.objectDataTab.plotArea')}*`}
                    helperText="max. 5000"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end" style={{ color: '#92969a' }}>
                          m2
                        </InputAdornment>
                      ),
                    }}
                  />
                </StyledCol>
              </StyledColContainer>
            </StyledRowContainer>
          </>
        )}
      </StyledObjectType>
      <StyledContinueButtonContainer>
        <Button kind="primary" onClick={() => onContinueClick()}>
          {t('common.continueButton')}
        </Button>
      </StyledContinueButtonContainer>
    </>
  )
}

export default ValueObjectTypeForm
